/**
 * DataQualityPanel — Phase 2.
 *
 * Renders the three-pane Data Quality Intelligence view (outliers,
 * duplicates, missing values). Same component is used both inside the
 * Program Intelligence Center (portfolio scope) AND inside the Program
 * Detail page (single-program scope) — only the data source differs.
 *
 * The parent provides the already-fetched `dq` payload so this component
 * stays purely presentational and is trivially testable.
 */
import { useState } from "react";
import {
  AlertTriangle,
  Copy,
  CalendarOff,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const TAB_DEFS = [
  { key: "outliers", label: "Outliers", icon: AlertTriangle, tone: "rose" },
  { key: "duplicates", label: "Duplicates", icon: Copy, tone: "amber" },
  { key: "missing", label: "Missing", icon: CalendarOff, tone: "slate" },
];

const TONE_CLASSES = {
  rose: {
    chip: "bg-rose-50 text-rose-700 border-rose-200",
    pillActive: "bg-rose-500 text-white",
    iconActive: "text-rose-500",
    accentText: "text-rose-700",
  },
  amber: {
    chip: "bg-amber-50 text-amber-800 border-amber-200",
    pillActive: "bg-amber-500 text-white",
    iconActive: "text-amber-500",
    accentText: "text-amber-700",
  },
  slate: {
    chip: "bg-slate-50 text-slate-700 border-slate-200",
    pillActive: "bg-slate-700 text-white",
    iconActive: "text-slate-500",
    accentText: "text-slate-700",
  },
};

export default function DataQualityPanel({ dq, loading, title = "Data Quality Intelligence", subtitle }) {
  const [tab, setTab] = useState("outliers");
  if (loading) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-sm p-5"
        data-testid="data-quality-loading"
      >
        <p className="label-overline">{title}</p>
        <div className="mt-3 space-y-2">
          <div className="h-3 bg-slate-100 rounded-sm w-2/3 animate-pulse" />
          <div className="h-3 bg-slate-100 rounded-sm w-1/2 animate-pulse" />
        </div>
      </div>
    );
  }
  if (!dq) return null;
  const s = dq.summary || {};
  const cleanScore = s.row_count > 0
    ? Math.round(100 - (s.outliers + s.duplicates) / Math.max(s.row_count, 1) * 100)
    : null;
  const isClean = (s.outliers || 0) + (s.duplicates || 0) + (s.missing || 0) === 0;

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-5"
      data-testid="data-quality-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="label-overline">{title}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DqChip
            label="Outliers"
            count={s.outliers || 0}
            tone="rose"
            active={tab === "outliers"}
            onClick={() => setTab("outliers")}
            testid="dq-chip-outliers"
          />
          <DqChip
            label="Duplicates"
            count={s.duplicates || 0}
            tone="amber"
            active={tab === "duplicates"}
            onClick={() => setTab("duplicates")}
            testid="dq-chip-duplicates"
          />
          <DqChip
            label="Missing"
            count={s.missing || 0}
            tone="slate"
            active={tab === "missing"}
            onClick={() => setTab("missing")}
            testid="dq-chip-missing"
          />
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-3">
        Audited <span className="font-mono">{s.row_count?.toLocaleString() || 0}</span> rows across
        <span className="font-mono"> {s.indicator_count?.toLocaleString() || 0}</span> indicators.
        {cleanScore != null && (
          <>
            {" · "}Numeric-row quality score:{" "}
            <span className={`font-medium ${cleanScore >= 95 ? "text-emerald-700" : cleanScore >= 80 ? "text-amber-700" : "text-rose-700"}`}>
              {cleanScore}%
            </span>
          </>
        )}
      </p>

      {isClean ? (
        <div
          className="mt-4 border border-dashed border-emerald-200 bg-emerald-50/40 rounded-sm p-6 text-center"
          data-testid="data-quality-clean"
        >
          <ShieldCheck className="w-7 h-7 text-emerald-500 mx-auto" />
          <p className="font-heading text-base font-medium mt-2 text-slate-900">Clean as a whistle</p>
          <p className="text-xs text-slate-500 mt-1">
            No outliers, duplicates, or gaps detected in this slice.
          </p>
        </div>
      ) : tab === "outliers" ? (
        <OutliersTable items={dq.outliers || []} />
      ) : tab === "duplicates" ? (
        <DuplicatesTable items={dq.duplicates || []} />
      ) : (
        <MissingTable items={dq.missing || []} />
      )}
    </div>
  );
}

function DqChip({ label, count, tone, active, onClick, testid }) {
  const t = TONE_CLASSES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border transition-colors ${
        active ? `${t.pillActive} border-transparent` : `${t.chip} hover:opacity-80`
      }`}
    >
      <span className="font-medium">{label}</span>
      <span className={`font-mono text-[10px] px-1 rounded-sm ${active ? "bg-white/20" : "bg-white/60"}`}>
        {count}
      </span>
    </button>
  );
}

function EmptyRow({ label }) {
  return (
    <div className="border border-dashed border-slate-200 bg-slate-50/60 rounded-sm p-6 text-center mt-3">
      <Sparkles className="w-5 h-5 text-slate-400 mx-auto" />
      <p className="text-xs text-slate-500 mt-2">No {label} detected — nice!</p>
    </div>
  );
}

function OutliersTable({ items }) {
  if (items.length === 0) return <EmptyRow label="outliers" />;
  return (
    <div className="mt-3 overflow-x-auto" data-testid="dq-outliers-table">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-slate-500 border-b border-slate-200">
            <th className="text-left font-medium py-2 pr-2">Indicator</th>
            <th className="text-left font-medium py-2 pr-2">Period</th>
            <th className="text-right font-medium py-2 pr-2">Value</th>
            <th className="text-right font-medium py-2 pr-2">Mean</th>
            <th className="text-right font-medium py-2 pr-2">σ</th>
            <th className="text-left font-medium py-2">Why</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 25).map((o) => (
            <tr
              key={o.row_id}
              className="border-b border-slate-100 hover:bg-rose-50/30"
              data-testid={`dq-outlier-row-${o.row_id}`}
            >
              <td className="py-2 pr-2 font-mono text-rose-700">{o.indicator_code || o.indicator_id?.slice(0, 8)}</td>
              <td className="py-2 pr-2 text-slate-500">{o.period || "—"}</td>
              <td className="py-2 pr-2 text-right font-mono text-slate-900">{o.value?.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right font-mono text-slate-500">{o.indicator_mean?.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right font-mono text-rose-600">{Math.abs(o.z_score).toFixed(1)}σ</td>
              <td className="py-2 text-slate-600">{o.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 25 && (
        <p className="text-[11px] text-slate-500 mt-2">
          Showing top 25 of {items.length} outliers (sorted by |σ|).
        </p>
      )}
    </div>
  );
}

function DuplicatesTable({ items }) {
  if (items.length === 0) return <EmptyRow label="duplicates" />;
  return (
    <div className="mt-3 overflow-x-auto" data-testid="dq-duplicates-table">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-slate-500 border-b border-slate-200">
            <th className="text-left font-medium py-2 pr-2">Indicator</th>
            <th className="text-left font-medium py-2 pr-2">Period</th>
            <th className="text-left font-medium py-2 pr-2">Disaggregation</th>
            <th className="text-right font-medium py-2 pr-2">Rows</th>
            <th className="text-left font-medium py-2">Values</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 25).map((d, idx) => (
            <tr
              key={`${d.indicator_id}-${d.period}-${idx}`}
              className="border-b border-slate-100 hover:bg-amber-50/30"
              data-testid={`dq-duplicate-row-${idx}`}
            >
              <td className="py-2 pr-2 font-mono text-amber-800">{d.indicator_code || d.indicator_id?.slice(0, 8)}</td>
              <td className="py-2 pr-2 text-slate-500">{d.period || "—"}</td>
              <td className="py-2 pr-2 text-slate-500 font-mono text-[11px]">{d.disaggregation}</td>
              <td className="py-2 pr-2 text-right text-amber-700 font-medium">{d.row_count}</td>
              <td className="py-2 text-slate-600 font-mono text-[11px]">
                {(d.values || []).slice(0, 4).map((v) => (v == null ? "—" : v.toLocaleString())).join(", ")}
                {(d.values || []).length > 4 && "…"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 25 && (
        <p className="text-[11px] text-slate-500 mt-2">
          Showing top 25 of {items.length} duplicate clusters.
        </p>
      )}
    </div>
  );
}

function MissingTable({ items }) {
  if (items.length === 0) return <EmptyRow label="missing periods" />;
  return (
    <div className="mt-3 overflow-x-auto" data-testid="dq-missing-table">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-slate-500 border-b border-slate-200">
            <th className="text-left font-medium py-2 pr-2">Indicator</th>
            <th className="text-left font-medium py-2 pr-2">State</th>
            <th className="text-right font-medium py-2 pr-2">Coverage</th>
            <th className="text-left font-medium py-2">Missing periods</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 25).map((m, idx) => (
            <tr
              key={`${m.indicator_id}-${idx}`}
              className="border-b border-slate-100 hover:bg-slate-50"
              data-testid={`dq-missing-row-${idx}`}
            >
              <td className="py-2 pr-2 font-mono text-slate-700">{m.indicator_code || m.indicator_id?.slice(0, 8)}</td>
              <td className="py-2 pr-2">
                <span className={`text-[10px] uppercase font-medium tracking-wider px-1.5 py-0.5 rounded-sm border ${
                  m.kind === "never_reported"
                    ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}>
                  {m.kind === "never_reported" ? "Never reported" : "Incomplete"}
                </span>
              </td>
              <td className="py-2 pr-2 text-right font-mono text-slate-700">{m.coverage_pct}%</td>
              <td className="py-2 text-slate-600 truncate max-w-[420px]" title={(m.missing_periods || []).join(", ")}>
                {(m.missing_periods || []).slice(0, 5).join(", ")}
                {(m.missing_periods || []).length > 5 && ` …+${m.missing_periods.length - 5}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 25 && (
        <p className="text-[11px] text-slate-500 mt-2">
          Showing top 25 of {items.length} indicators with gaps.
        </p>
      )}
    </div>
  );
}
